
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { MicroSkillAnalysis } from "@/types/quiz";
import { 
  BarChart3, 
  BrainCircuit, 
  ChevronUp, 
  ChevronDown, 
  Target, 
  Sparkles, 
  AlertTriangle 
} from "lucide-react";
import { cn } from "@/lib/utils";

interface MicroSkillAnalyticsProps {
  microSkillsAnalysis: MicroSkillAnalysis[];
}

const MicroSkillAnalytics: React.FC<MicroSkillAnalyticsProps> = ({ microSkillsAnalysis }) => {
  // Group skills by strength
  const strongSkills = microSkillsAnalysis.filter(skill => skill.strength === 'strong');
  const moderateSkills = microSkillsAnalysis.filter(skill => skill.strength === 'moderate');
  const weakSkills = microSkillsAnalysis.filter(skill => skill.strength === 'weak');
  
  // Generate insight text based on analysis
  const generateInsights = () => {
    const insights = [];
    
    if (strongSkills.length > 0) {
      insights.push(`You're strong in ${strongSkills.slice(0, 2).map(s => s.skill).join(' and ')}${strongSkills.length > 2 ? ' and other areas' : ''}.`);
    }
    
    if (weakSkills.length > 0) {
      insights.push(`Focus on improving ${weakSkills.slice(0, 2).map(s => s.skill).join(' and ')}${weakSkills.length > 2 ? ' and other weak areas' : ''}.`);
    }
    
    if (moderateSkills.length > 0) {
      insights.push(`You have moderate proficiency in ${moderateSkills.slice(0, 2).map(s => s.skill).join(' and ')}${moderateSkills.length > 2 ? ' and similar topics' : ''}.`);
    }
    
    return insights;
  };
  
  const insights = generateInsights();
  
  if (!microSkillsAnalysis || microSkillsAnalysis.length === 0) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6 text-center">
          <p className="text-gray-500">No micro skill data available for this test.</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl flex items-center text-primary">
          <BrainCircuit className="mr-2 h-5 w-5" />
          Micro Skill Analytics
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-6">
          {/* Insights Section */}
          <div className="bg-secondary/20 p-4 rounded-lg">
            <h3 className="text-md font-medium mb-2 flex items-center">
              <Sparkles className="h-4 w-4 text-yellow-500 mr-2" />
              Key Insights
            </h3>
            <ul className="space-y-1">
              {insights.map((insight, index) => (
                <li key={index} className="text-sm flex items-start">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary mt-1.5 mr-2 flex-shrink-0"></span>
                  {insight}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Strong Skills Section */}
          {strongSkills.length > 0 && (
            <div>
              <h3 className="text-md font-medium mb-3 flex items-center">
                <ChevronUp className="h-4 w-4 text-green-500 mr-2" />
                Strong Areas
              </h3>
              <div className="space-y-3">
                {strongSkills.map((skill, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{skill.skill}</span>
                      <span className="font-medium text-green-600">{skill.percentage.toFixed(0)}%</span>
                    </div>
                    <Progress 
                      value={skill.percentage} 
                      className="h-2" 
                      indicatorClassName="bg-green-500" 
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{skill.correctAnswers}/{skill.totalQuestions} correct</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Strong
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Moderate Skills Section */}
          {moderateSkills.length > 0 && (
            <div>
              <h3 className="text-md font-medium mb-3 flex items-center">
                <BarChart3 className="h-4 w-4 text-blue-500 mr-2" />
                Moderate Areas
              </h3>
              <div className="space-y-3">
                {moderateSkills.map((skill, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{skill.skill}</span>
                      <span className="font-medium text-blue-600">{skill.percentage.toFixed(0)}%</span>
                    </div>
                    <Progress 
                      value={skill.percentage} 
                      className="h-2" 
                      indicatorClassName="bg-blue-500" 
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{skill.correctAnswers}/{skill.totalQuestions} correct</span>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        Moderate
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Weak Skills Section */}
          {weakSkills.length > 0 && (
            <div>
              <h3 className="text-md font-medium mb-3 flex items-center">
                <ChevronDown className="h-4 w-4 text-red-500 mr-2" />
                Focus Areas
              </h3>
              <div className="space-y-3">
                {weakSkills.map((skill, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{skill.skill}</span>
                      <span className="font-medium text-red-600">{skill.percentage.toFixed(0)}%</span>
                    </div>
                    <Progress 
                      value={skill.percentage} 
                      className="h-2" 
                      indicatorClassName="bg-red-500" 
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{skill.correctAnswers}/{skill.totalQuestions} correct</span>
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        Needs Improvement
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Recommendation */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h3 className="text-md font-medium mb-2 flex items-center">
              <Target className="h-4 w-4 text-purple-500 mr-2" />
              Improvement Strategy
            </h3>
            {weakSkills.length > 0 ? (
              <p className="text-sm text-gray-700">
                Focus on strengthening your weak areas by practicing more questions in {weakSkills.slice(0, 3).map(s => s.skill).join(', ')}. 
                Try to understand the fundamental concepts behind these topics and allocate dedicated study time for them.
              </p>
            ) : (
              <p className="text-sm text-gray-700">
                You're performing well across most micro skills. To further improve, challenge yourself with 
                more difficult questions in your already strong areas while maintaining your current knowledge.
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MicroSkillAnalytics;
